/**
 * Redux reducer for Contact Search
 */

import {
    CONTACT_SEARCH_SET_STATUS,
    CONTACT_SEARCH_SET_RESULTS,
    CONTACT_SEARCH_SET_FIRST,
    CONTACT_SEARCH_SET_ROWS,
    CONTACT_SEARCH_SET_CURRENT_PAGE,
    CONTACT_SEARCH_SET_TOTAL_PAGES,
    CONTACT_SEARCH_SET_FILTER,
    CONTACT_SEARCH_SET_SORT,
    CONTACT_SEARCH_SET_SEARCH_TERM,
    CONTACT_SEARCH_RESET,
    CONTACT_SEARCH_WORKS_SET_FETCH_STATUS,
    CONTACT_SEARCH_WORKS_SET_FETCH_RESULTS,
    CONTACT_SEARCH_WORKS_SET_CURRENT_PAGE,
    CONTACT_SEARCH_WORKS_SET_TOTAL_PAGES,
    CONTACT_SEARCH_WORKS_SET_ROWS,
    CONTACT_SEARCH_WORKS_SET_FIRST,
    CONTACT_SEARCH_WORKS_SET_FILTER,
    CONTACT_SEARCH_WORKS_SET_SORT,
    CONTACT_SEARCH_WORKS_INIT,
    ADD_POTENTIAL_MEMBER_SET_FIELD,
    ADD_POTENTIAL_MEMBER_SET_ERRORS,
    ADD_POTENTIAL_MEMBER_SET_STATUS,
    ADD_POTENTIAL_MEMBER_RESET
} from './actions'

export const addPotentialMemberInitialState = {
    firstName: '',
    middleName: '',
    lastName: '',
    remarks: '',
    errors: {},
    status: 'idle'
}

export const contactSearchInitialState = {
    status: 'idle',
    results: [],
    totalCount: 0,
    currentPage: 1,
    totalPages: 0,
    rows: 50,
    first: 0,
    sort: null,
    filter: null,
    searchTerm: null,
    works: {
        // Store works data by contactId for lazy loading (>50 works)
        byContactId: {}
    },
    addPotentialMember: addPotentialMemberInitialState
}

export const contactSearchReducer = (state = contactSearchInitialState, action) => {
    switch (action.type) {
        case CONTACT_SEARCH_SET_STATUS:
            return { ...state, status: action.payload }

        case CONTACT_SEARCH_SET_RESULTS:
            return {
                ...state,
                results: action.payload.results,
                totalCount: action.payload.totalCount
            }

        case CONTACT_SEARCH_SET_FIRST:
            return { ...state, first: action.payload }

        case CONTACT_SEARCH_SET_ROWS:
            return { ...state, rows: action.payload }

        case CONTACT_SEARCH_SET_CURRENT_PAGE:
            return { ...state, currentPage: action.payload }

        case CONTACT_SEARCH_SET_TOTAL_PAGES:
            return { ...state, totalPages: action.payload }

        case CONTACT_SEARCH_SET_FILTER:
            return { ...state, filter: action.payload }

        case CONTACT_SEARCH_SET_SORT:
            return { ...state, sort: action.payload }

        case CONTACT_SEARCH_SET_SEARCH_TERM:
            return {
                ...state,
                searchTerm: action.payload,
                filter: null,
                sort: null,
                first: 0,
                currentPage: 1
            }

        case CONTACT_SEARCH_RESET:
            return contactSearchInitialState

        // Contact Works Reducers
        case CONTACT_SEARCH_WORKS_SET_FETCH_STATUS: {
            const { contactId, status } = action.payload
            return {
                ...state,
                works: {
                    ...state.works,
                    byContactId: {
                        ...state.works.byContactId,
                        [contactId]: {
                            ...state.works.byContactId[contactId],
                            status
                        }
                    }
                }
            }
        }

        case CONTACT_SEARCH_WORKS_SET_FETCH_RESULTS: {
            const { contactId, results } = action.payload
            return {
                ...state,
                works: {
                    ...state.works,
                    byContactId: {
                        ...state.works.byContactId,
                        [contactId]: {
                            ...state.works.byContactId[contactId],
                            results
                        }
                    }
                }
            }
        }

        case CONTACT_SEARCH_WORKS_SET_CURRENT_PAGE: {
            const { contactId, currentPage } = action.payload
            return {
                ...state,
                works: {
                    ...state.works,
                    byContactId: {
                        ...state.works.byContactId,
                        [contactId]: {
                            ...state.works.byContactId[contactId],
                            currentPage,
                            status: 'idle'  // Reset status to allow fetch
                        }
                    }
                }
            }
        }

        case CONTACT_SEARCH_WORKS_SET_TOTAL_PAGES: {
            const { contactId, totalPages } = action.payload
            return {
                ...state,
                works: {
                    ...state.works,
                    byContactId: {
                        ...state.works.byContactId,
                        [contactId]: {
                            ...state.works.byContactId[contactId],
                            totalPages
                        }
                    }
                }
            }
        }

        case CONTACT_SEARCH_WORKS_SET_ROWS: {
            const { contactId, row } = action.payload
            return {
                ...state,
                works: {
                    ...state.works,
                    byContactId: {
                        ...state.works.byContactId,
                        [contactId]: {
                            ...state.works.byContactId[contactId],
                            row,
                            status: 'idle'  // Reset status to allow fetch
                        }
                    }
                }
            }
        }

        case CONTACT_SEARCH_WORKS_SET_FIRST: {
            const { contactId, first } = action.payload
            return {
                ...state,
                works: {
                    ...state.works,
                    byContactId: {
                        ...state.works.byContactId,
                        [contactId]: {
                            ...state.works.byContactId[contactId],
                            first,
                            status: 'idle'  // Reset status to allow fetch
                        }
                    }
                }
            }
        }

        case CONTACT_SEARCH_WORKS_SET_FILTER: {
            const { contactId, filter } = action.payload
            return {
                ...state,
                works: {
                    ...state.works,
                    byContactId: {
                        ...state.works.byContactId,
                        [contactId]: {
                            ...state.works.byContactId[contactId],
                            filter,
                            status: 'idle'  // Reset status to allow fetch
                        }
                    }
                }
            }
        }

        case CONTACT_SEARCH_WORKS_SET_SORT: {
            const { contactId, sort } = action.payload
            return {
                ...state,
                works: {
                    ...state.works,
                    byContactId: {
                        ...state.works.byContactId,
                        [contactId]: {
                            ...state.works.byContactId[contactId],
                            sort,
                            status: 'idle'  // Reset status to allow fetch
                        }
                    }
                }
            }
        }

        case CONTACT_SEARCH_WORKS_INIT: {
            const { contactId } = action.payload
            // Initialize state for a new contact if it doesn't exist
            if (!state.works.byContactId[contactId]) {
                return {
                    ...state,
                    works: {
                        ...state.works,
                        byContactId: {
                            ...state.works.byContactId,
                            [contactId]: {
                                status: 'idle',
                                results: { total: 0, results: [] },
                                currentPage: 1,
                                totalPages: 0,
                                row: 50,
                                first: 0,
                                sort: null,
                                filter: null
                            }
                        }
                    }
                }
            }
            return state
        }

        // Add Potential Member Reducers
        case ADD_POTENTIAL_MEMBER_SET_FIELD:
            return {
                ...state,
                addPotentialMember: {
                    ...state.addPotentialMember,
                    [action.payload.field]: action.payload.value,
                    errors: { ...state.addPotentialMember.errors, [action.payload.field]: null }
                }
            }

        case ADD_POTENTIAL_MEMBER_SET_ERRORS:
            return {
                ...state,
                addPotentialMember: { ...state.addPotentialMember, errors: action.payload }
            }

        case ADD_POTENTIAL_MEMBER_SET_STATUS:
            return {
                ...state,
                addPotentialMember: { ...state.addPotentialMember, status: action.payload }
            }

        case ADD_POTENTIAL_MEMBER_RESET:
            return {
                ...state,
                addPotentialMember: addPotentialMemberInitialState
            }

        default:
            return state
    }
}

export default contactSearchReducer
